import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/apiClient";
import SkillSlider from "@/components/SkillSlider";
import EducationSection, { blankDegree } from "@/components/EducationSection";
import { toast } from "sonner";
import { SlidersHorizontal, Loader2, Sparkles, GraduationCap } from "lucide-react";

const LEVEL_BY_IDX = ["none", "basic", "production"];
const IDX_BY_LEVEL = { none: 0, basic: 1, production: 2 };

export default function Assess() {
  const { user, refreshUser } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [skills, setSkills] = useState([]);
  const [ratings, setRatings] = useState({});
  const [degrees, setDegrees] = useState([blankDegree()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.education?.length) setDegrees(user.education);
  }, [user]);

  const load = async () => {
    setLoading(true);
    const { data: td } = await api.get("/tracks");
    setTracks(td.tracks);
    if (td.tracks.length) {
      const ids = td.tracks.map((t) => t.career_id).join(",");
      const { data } = await api.get("/skills/for-careers", { params: { career_ids: ids, limit: 24 } });
      setSkills(data.skills);
      setRatings(Object.fromEntries(data.skills.map((s) => [s.skill_id, IDX_BY_LEVEL[s.user_level] ?? 0])));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { education: degrees.filter((d) => d.degree) });
      const assessments = skills.map((s) => ({ skill_id: s.skill_id, level: LEVEL_BY_IDX[ratings[s.skill_id] || 0] }));
      const { data } = await api.post("/self-assessment", { assessments });
      toast.success(`Baseline refreshed · ${data.skills_added} skill(s) updated`);
      await refreshUser();
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (loading) return <div className="h-96 flex items-center justify-center"><Loader2 className="animate-spin text-[#6366F1]" /></div>;

  if (tracks.length === 0)
    return (
      <div className="max-w-lg">
        <h1 className="font-display font-extrabold text-3xl tracking-tight text-white">Self-Assessment</h1>
        <div className="bg-white rounded-2xl p-8 border border-dashed border-slate-300 mt-6 text-center">
          <p className="text-slate-500">Add career tracks first to assess their skills.</p>
          <Link to="/onboarding" className="inline-block mt-3 text-[#6366F1] font-semibold hover:underline">Add dream careers →</Link>
        </div>
      </div>
    );

  return (
    <div className="max-w-3xl">
      <p className="eyebrow text-slate-400 flex items-center gap-1.5"><SlidersHorizontal size={13} /> SELF-ASSESSMENT</p>
      <h1 className="font-display font-extrabold text-3xl tracking-tight text-white mt-1">Refresh your baseline</h1>
      <p className="text-slate-400 text-sm mt-2 mb-6">Slide every core skill across your {tracks.length} track(s) to your honest current level. Saving recomputes all readiness scores in one go.</p>

      <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-5">
        <div className="flex items-center gap-2">
          <GraduationCap size={16} className="text-[#6366F1]" />
          <h2 className="font-bold text-slate-900 text-sm">Education</h2>
        </div>
        <p className="text-xs text-slate-500 mt-1">Keep your degrees and semester grades up to date — they power your resume.</p>
        <EducationSection degrees={degrees} setDegrees={setDegrees} />
      </div>

      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <div className="space-y-4 max-h-[560px] overflow-y-auto pr-1">
          {skills.map((s) => (
            <div key={s.skill_id} data-testid={`assess-row-${s.skill_id}`} className="p-4 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{s.skill_name}</p>
                  {s.essential && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded shrink-0">ESSENTIAL</span>}
                </div>
                <span className="text-[11px] font-mono text-slate-400 shrink-0">w {s.weight}</span>
              </div>
              <SkillSlider value={ratings[s.skill_id] || 0} testid={`slider-${s.skill_id}`}
                onChange={(v) => setRatings({ ...ratings, [s.skill_id]: v })} />
            </div>
          ))}
        </div>
        <button data-testid="assess-save-btn" onClick={save} disabled={saving}
          className="mt-6 w-full flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] active:scale-[0.98] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Save & recompute readiness
        </button>
      </div>
    </div>
  );
}
